import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { coursesAPI } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { BookOpen, Clock, Play, CheckCircle, Award, TrendingUp } from 'lucide-react';

interface Enrollment {
  _id: string;
  progress: number;
  currentLesson: number;
  completedLessons: number[];
  certificateIssued: boolean;
  certificateUrl?: string;
  enrolledAt: string;
  completedAt?: string;
  lastAccessedAt: string;
  course: {
    _id: string;
    title: string;
    description: string;
    thumbnail?: string;
    image?: string;
    duration: number;
    level: string;
    instructor: { name?: string; firstName?: string; lastName?: string };
  };
}

const MyCourses: React.FC = () => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'in-progress' | 'completed'>('all');

  useEffect(() => {
    loadEnrollments();
  }, []);

  const loadEnrollments = async () => {
    try {
      setLoading(true);
      const response = await coursesAPI.getMyEnrollments();
      setEnrollments(response.data.data || []);
    } catch (error: any) {
      console.error('Error loading enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInstructorName = (instructor: Enrollment['course']['instructor']) => {
    if (instructor?.firstName && instructor?.lastName) return `${instructor.firstName} ${instructor.lastName}`;
    return instructor?.name || 'Instructor';
  };

  const getLevelLabel = (level: string) => {
    const labels: Record<string, string> = { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' };
    return labels[level] || level;
  };

  const getThumbnailUrl = (course: Enrollment['course']) => {
    const path = course.thumbnail || course.image;
    return path ? (path.startsWith('http') ? path : getImageUrl(path)) : null;
  };

  const filteredEnrollments = enrollments.filter((e) => {
    if (filter === 'completed') return e.progress === 100;
    if (filter === 'in-progress') return e.progress > 0 && e.progress < 100;
    return true;
  });

  const completedCount = enrollments.filter((e) => e.progress === 100).length;
  const inProgressCount = enrollments.filter((e) => e.progress > 0 && e.progress < 100).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Courses</h1>
          <p className="text-gray-600">Continue your learning journey</p>
        </div>
        <Link
          to="/create-course"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
        >
          <span>+</span>
          <span>Create Course</span>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Courses</p>
              <p className="text-2xl font-bold text-gray-900">{enrollments.length}</p>
            </div>
            <BookOpen className="h-12 w-12 text-emerald-600 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{inProgressCount}</p>
            </div>
            <TrendingUp className="h-12 w-12 text-blue-600 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
            </div>
            <CheckCircle className="h-12 w-12 text-emerald-600 opacity-50" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex gap-2">
          {(['all', 'in-progress', 'completed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg transition-colors ${filter === f ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {f === 'all' && `All (${enrollments.length})`}
              {f === 'in-progress' && `In Progress (${inProgressCount})`}
              {f === 'completed' && `Completed (${completedCount})`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      ) : filteredEnrollments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {filter === 'all' ? 'No courses' : filter === 'completed' ? 'No completed courses' : 'No courses in progress'}
          </h3>
          {filter === 'all' && (
            <Link to="/courses" className="inline-block mt-4 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
              Explore Courses
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEnrollments.map((enrollment) => {
            const thumbUrl = getThumbnailUrl(enrollment.course);
            return (
              <div key={enrollment._id} className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow overflow-hidden flex flex-col">
                {thumbUrl ? (
                  <img src={thumbUrl} alt={enrollment.course.title} className="w-full h-48 object-cover" />
                ) : (
                  <div className="w-full h-48 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <BookOpen className="h-16 w-16 text-white opacity-50" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <span className="inline-block px-2 py-1 text-xs font-semibold rounded bg-emerald-100 text-emerald-800 mb-2">{getLevelLabel(enrollment.course.level)}</span>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900 line-clamp-2">{enrollment.course.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 flex-1 line-clamp-2">{enrollment.course.description}</p>
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">Progress</span>
                      <span className="font-semibold text-gray-900">{enrollment.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-emerald-600 h-2 rounded-full transition-all" style={{ width: `${enrollment.progress}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span className="flex items-center"><Clock className="h-4 w-4 mr-1" />{enrollment.course.duration} min</span>
                    {enrollment.progress === 100 && (
                      <span className="flex items-center text-emerald-600"><CheckCircle className="h-4 w-4 mr-1" />Completed</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/courses/${enrollment.course._id}`} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2">
                      <Play className="h-4 w-4" />{enrollment.progress === 100 ? 'Review' : 'Continue'}
                    </Link>
                    {enrollment.certificateIssued && enrollment.certificateUrl && (
                      <a href={enrollment.certificateUrl} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center" title="Download Certificate">
                        <Award className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
};

export default MyCourses;
